/**
 * NotificationBell — header bell icon with unread count and dropdown list.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useStore } from '../store';
import { Bell, Check, CheckCheck, FileText, ShieldCheck, AlertCircle, X, KeyRound, Lock, Unlock, ShieldAlert, Bug } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { AppNotification } from '../types/notification';

/**
 * The 4 site edit-lock (#82) notification types. Their notifications carry
 * `{ siteId, siteName }` (SiteLockNotification variant) so clicking navigates
 * to the site rather than a report.
 */
const SITE_LOCK_NOTIFICATION_TYPES = [
  'edit-access-requested',
  'edit-access-approved',
  'edit-access-denied',
  'lock-force-unlocked',
] as const;

function isSiteLockNotification(
  notif: AppNotification,
): notif is Extract<AppNotification, { siteId: string }> {
  return (SITE_LOCK_NOTIFICATION_TYPES as readonly string[]).includes(notif.type);
}

export function NotificationBell() {
  const navigate = useNavigate();
  const { notificationState, loadNotifications, markNotificationRead, markAllNotificationsRead } = useStore();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Load on mount — poll every 5 mins (was 60s, too aggressive)
  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 300000);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const { notifications, unreadCount } = notificationState;

  const handleClick = (notif: AppNotification) => {
    if (!notif.read) markNotificationRead(notif.id);
    if (notif.type === 'report-pdf-ready' || notif.type === 'report-pdf-failed') {
      navigate(`/sites/${encodeURIComponent(notif.siteId)}/reports/${encodeURIComponent(notif.reportId)}`);
    } else if (isSiteLockNotification(notif)) {
      // Site edit-lock (#82) — navigate to the site. setViewMode flips the
      // engine to SiteRAG; setActiveSite syncs `/sites/{id}` to the URL, which
      // the App route effect derives into the 'site' sub-view.
      useStore.getState().setViewMode('siterag');
      useStore.getState().setActiveSite(notif.siteId);
    } else if ('reportId' in notif && notif.reportId) {
      // Navigate to the report. The `'reportId' in notif` guard narrows away
      // members that carry no report reference (e.g. the #119 'bug-report'
      // notification), which simply don't navigate on click.
      useStore.getState().setActiveRagReport(notif.reportId);
      useStore.getState().setViewMode('siterag');
    }
    setOpen(false);
  };

  const iconForType = (type: AppNotification['type']) => {
    switch (type) {
      case 'approval-requested': return <ShieldCheck className="w-3.5 h-3.5 text-blue-500" />;
      case 'approval-approved': return <Check className="w-3.5 h-3.5 text-success" />;
      case 'approval-final': return <CheckCheck className="w-3.5 h-3.5 text-success" />;
      case 'approval-rejected': return <X className="w-3.5 h-3.5 text-danger" />;
      case 'approval-changes': return <AlertCircle className="w-3.5 h-3.5 text-warning" />;
      case 'report-pdf-ready': return <FileText className="w-3.5 h-3.5 text-success" />;
      case 'report-pdf-failed': return <AlertCircle className="w-3.5 h-3.5 text-warning" />;
      case 'report-shared': return <FileText className="w-3.5 h-3.5 text-indigo-500" />;
      // Site edit-lock (#82) notification types.
      case 'edit-access-requested': return <KeyRound className="w-3.5 h-3.5 text-blue-500" />;
      case 'edit-access-approved': return <Unlock className="w-3.5 h-3.5 text-success" />;
      case 'edit-access-denied': return <Lock className="w-3.5 h-3.5 text-danger" />;
      case 'lock-force-unlocked': return <ShieldAlert className="w-3.5 h-3.5 text-warning" />;
      // Bug report (#119) notification type.
      case 'bug-report': return <Bug className="w-3.5 h-3.5 text-warning" />;
      default: return <Bell className="w-3.5 h-3.5 text-text-secondary" />;
    }
  };

  const timeAgo = (iso: string) => {
    const ts = new Date(iso).getTime();
    if (isNaN(ts)) return '';
    const diff = Date.now() - ts;
    if (diff < 0) return 'just now';
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <div ref={ref} className="relative">
      <button
        className="relative p-2 hover:bg-state-hover transition-colors"
        onClick={() => setOpen(!open)}
        title="Notifications"
      >
        <Bell className="w-4 h-4 text-text-secondary" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-danger text-text-primary text-[9px] font-bold flex items-center justify-center rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-80 bg-background border border-border rounded-lg shadow-lg z-50 max-h-96 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-border-subtle">
            <span className="text-[10px] font-bold uppercase tracking-widest text-text-secondary">
              Notifications {unreadCount > 0 && `(${unreadCount})`}
            </span>
            {unreadCount > 0 && (
              <button
                className="text-[9px] text-blue-600 hover:underline uppercase tracking-widest font-bold"
                onClick={markAllNotificationsRead}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="overflow-y-auto flex-1">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-[10px] text-text-secondary uppercase tracking-widest">
                No notifications
              </div>
            ) : (
              notifications.map(notif => (
                <button
                  key={notif.id}
                  className={`w-full text-left px-3 py-2.5 border-b border-border-subtle hover:bg-muted transition-colors flex items-start gap-2 ${
                    !notif.read ? 'bg-primary/5' : ''
                  }`}
                  onClick={() => handleClick(notif)}
                >
                  <div className="mt-0.5 shrink-0">{iconForType(notif.type)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-[10px] font-bold ${!notif.read ? 'text-text-primary' : 'text-text-muted'}`}>
                        {notif.title}
                      </span>
                      <span className="text-[8px] text-text-secondary shrink-0">{timeAgo(notif.createdAt)}</span>
                    </div>
                    <p className={`text-[9px] mt-0.5 ${!notif.read ? 'text-foreground' : 'text-text-secondary'}`}>
                      {notif.message}
                    </p>
                  </div>
                  {!notif.read && (
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
